import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature } from "geojson";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api/layersSlice";
import { buildPathGraph } from "fond/draw/modes/base/pathGraph";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import {
  DeleteButton,
  LineStringButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  ShortestPathButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { isAnyLineString } from "fond/types/geojson";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<LineStringButton key="drawLine" />, <ShortestPathButton key="shortestPath" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const preservePropertiesFor = (layerId: string) => (feature: Feature) => ({
  layerId,
  ...(feature.properties?.sourcePathId != null && { sourcePathId: feature.properties.sourcePathId }),
});

const MuPowerFootprintDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const ugPathLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.ugPath }));
  const { data: ugPathFeatures, isLoading: ugPathLoading } = useGetFeaturesQuery({ layerId: ugPathLayer?.ID ?? "" }, { skip: !ugPathLayer?.ID });

  const ugPathGraph = useMemo(() => {
    if (!ugPathFeatures) return undefined;
    return buildPathGraph(ugPathFeatures.filter(isAnyLineString), { splitIntersectingPaths: true });
  }, [ugPathFeatures]);

  // We don't want to render anything until the path graph has been calculated
  const awaitingGraph = !!ugPathLayer?.ID && ugPathLoading;

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      shortestPath: ugPathGraph ? { graph: ugPathGraph } : undefined,
      editOptions: {
        allLayers: [
          {
            ID: MuPowerSolverLayerIds.primaryFootprint,
            GeometryType: "LineString",
            getFeatureProperties: preservePropertiesFor(MuPowerSolverLayerIds.primaryFootprint),
          },
          {
            ID: MuPowerSolverLayerIds.secondaryFootprint,
            GeometryType: "LineString",
            getFeatureProperties: preservePropertiesFor(MuPowerSolverLayerIds.secondaryFootprint),
          },
        ],
        editingLayerId: MuPowerSolverLayerIds.primaryFootprint,
        editingLayerIds: [MuPowerSolverLayerIds.primaryFootprint, MuPowerSolverLayerIds.secondaryFootprint],
      },
    }),
    [ugPathGraph]
  );

  if (awaitingGraph) {
    return <BlockSpinner />;
  }

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent title="Edit footprint" alertTitle="Draw footprint" onFinish={clear} />
    </MultiLayerEditProvider>
  );
};

export default MuPowerFootprintDraw;
